/**
 * Module dependencies.
 */

import { cn } from 'app/lib/utils';
import React, { ReactNode } from 'react';

/**
 * `Props` type.
 */

type Props = {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  className?: string;
  size?: 'md' | 'lg';
};

/**
 * Export `SectionHeader` component.
 *
 * The shared icon + title + description header used by modals and standalone
 * pages. Kept in one place so the spacing stays consistent everywhere (title and
 * description sit tight together via `leading-tight`, no gap between them).
 */

export function SectionHeader({ icon, title, description, className, size = 'md' }: Props): JSX.Element {
  return (
    <div className={cn('flex items-center gap-3', className)}>
      {icon ? (
        <span
          className={cn(
            'grid shrink-0 place-items-center rounded-lg bg-primary/10 text-primary',
            size === 'lg' ? 'h-11 w-11' : 'h-10 w-10'
          )}
        >
          {icon}
        </span>
      ) : null}

      <div className={'min-w-0'}>
        <h2
          className={cn(
            'm-0 truncate font-semibold leading-tight text-foreground',
            size === 'lg' ? 'text-lg' : 'text-base'
          )}
        >
          {title}
        </h2>

        {description ? (
          <p className={'m-0 truncate text-xs leading-tight text-muted-foreground'}>{description}</p>
        ) : null}
      </div>
    </div>
  );
}
